/**
 * Session-gated fetch for PostgREST.
 *
 * supabase-js attaches `Authorization: Bearer <token>` per request, and when
 * there is no session at that moment it silently falls back to the ANON key
 * (supabase-js lib/fetch.ts: `realToken ?? supabaseKey`). Those requests run as
 * the `anon` role and produce the 401/42501 ("permission denied for table
 * homeowner_members", "... for view objects") and profiles 406 storm, and the
 * fail-open services used to turn them into cached "success" defaults.
 *
 * This wrapper is installed as the client's `global.fetch`, so it sees every
 * `supabase.from()` / `.rpc()` call AFTER supabase-js has chosen the header.
 * It only touches a request when ALL of these hold:
 *   - same origin as the Supabase URL and path under `/rest/v1/`;
 *   - `Authorization` is exactly `Bearer <anonKey>` (a user JWT is the happy
 *     path and passes through untouched: one URL parse + one header read);
 *   - the resource is NOT in the public allowlist (signed-out invitation pages).
 * Such a request is never sent with the anon key:
 *   - after a known sign-out (`markSignedOut()` / SIGNED_OUT) it is answered
 *     locally with a synthetic 401 `{ code: 'AUTH_SESSION_MISSING' }`;
 *   - otherwise it waits (one shared auth listener for all waiters) up to
 *     `waitMs` for a session and is then sent with `Bearer <access_token>`, or
 *     answered with the same synthetic 401 on timeout.
 * postgrest-js parses the synthetic body like a real PostgREST error, so
 * callers see `{ error: { code: 'AUTH_SESSION_MISSING' }, status: 401 }`.
 *
 * `/auth/v1`, `/storage`, `/functions`, realtime and other origins ALWAYS pass
 * straight through: the auth client uses this same fetch for session restore
 * and token refresh, and waiting on a session is only possible while those
 * go out — gating them would deadlock.
 *
 * Scope, honestly stated: this removes the anon request NOISE (S1: the logout
 * race; short cold/refresh gaps). It does NOT outlast auth-js's 60 s
 * refresh-failure cooldown (REFRESH_FAILURE_COOLDOWN_MS) — in that window the
 * wait times out into the local 401. Recovery from such a gap (S2) is the
 * TOKEN_REFRESHED / SIGNED_IN refetch of errored queries in AuthProvider.
 *
 * Types-only imports: lib/queryClient imports SESSION_MISSING_CODE from here,
 * so this module must never pull in the Supabase client (no import cycles).
 *
 * @module services/supabase/sessionGatedFetch
 */
import type { AuthChangeEvent, Session } from '@supabase/supabase-js';

/** Error code of the synthetic 401 the gate answers with. PostgREST never sends it. */
export const SESSION_MISSING_CODE = 'AUTH_SESSION_MISSING';

/**
 * True for an error a NEW session can heal: the gate's local 401, a rejected
 * JWT (PGRST301 invalid, PGRST303 expired), the anon role's permission denied
 * (42501 — a request that slipped out without the user's token), or any
 * HTTP-401-shaped error. ThingsBoard 5xx, network and data errors are not.
 * AuthProvider re-runs only these when a session (re)appears.
 */
export function isSessionAuthError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const { code, status } = error as { code?: unknown; status?: unknown };
  return (
    code === SESSION_MISSING_CODE ||
    code === 'PGRST301' ||
    code === 'PGRST303' ||
    code === '42501' ||
    status === 401
  );
}

const DEFAULT_WAIT_MS = 5000;

/** What the gate did with an anon-keyed PostgREST request (for DEV diagnostics). */
export type SessionGateOutcome = 'blocked' | 'waiting' | 'resumed';

export interface SessionGateDeps {
  supabaseUrl: string;
  anonKey: string;
  /** `/rest/v1` resources signed-out pages may call with the anon key: 'table' or 'rpc/<fn>'. */
  publicRestResources: ReadonlySet<string>;
  /** Registers an auth listener and returns its unsubscribe. */
  onAuthStateChange: (
    callback: (event: AuthChangeEvent, session: Session | null) => void,
  ) => () => void;
  /** The real transport. Late-bound in the app so Capacitor's native fetch patch is honoured. */
  baseFetch: typeof fetch;
  /** Longest an anon-keyed request waits for a session before the local 401. Default 5000. */
  waitMs?: number;
  /** Diagnostics hook. Receives the resource name only — never headers or tokens. */
  onGate?: (resource: string, outcome: SessionGateOutcome) => void;
}

export interface SessionGate {
  fetch: typeof fetch;
  /** Call BEFORE signOut: from then on no PostgREST request leaves with the anon key. */
  markSignedOut: () => void;
  /** Registers the permanent signed-in/signed-out tracker. Idempotent. */
  trackAuth: () => void;
}

interface Waiter {
  settle: (session: Session | null) => void;
}

const isRequest = (input: RequestInfo | URL): input is Request =>
  typeof Request !== 'undefined' && input instanceof Request;

const requestUrl = (input: RequestInfo | URL): string => {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
};

/** `https://host[/prefix]/rest/v1/`, or null when the Supabase URL is unusable (gate disabled). */
const restPrefixOf = (supabaseUrl: string): string | null => {
  try {
    const base = new URL('rest/v1/', supabaseUrl.endsWith('/') ? supabaseUrl : `${supabaseUrl}/`);
    return base.origin + base.pathname;
  } catch {
    return null;
  }
};

const decodeSegment = (segment: string): string => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

const abortErrorOf = (signal: AbortSignal | null | undefined): unknown =>
  // Mirror fetch(): reject with the signal's reason (AbortError by default).
  signal?.reason !== undefined ? signal.reason : new DOMException('The operation was aborted.', 'AbortError');

export function createSessionGate(deps: SessionGateDeps): SessionGate {
  const waitMs = deps.waitMs ?? DEFAULT_WAIT_MS;
  const restPrefix = restPrefixOf(deps.supabaseUrl);
  const anonBearer = `Bearer ${deps.anonKey}`;

  let signedOut = false;
  let tracking = false;
  const waiters = new Set<Waiter>();
  let unsubscribe: (() => void) | null = null;

  /** 'table' | 'rpc/<fn>' for a same-origin /rest/v1 request, otherwise null (pass through). */
  const restResource = (input: RequestInfo | URL): string | null => {
    if (!restPrefix) return null;
    let url: URL;
    try {
      url = new URL(requestUrl(input));
    } catch {
      return null; // relative / malformed: not a Supabase REST call
    }
    const path = url.origin + url.pathname;
    if (!path.startsWith(restPrefix)) return null;
    const [first = '', second = ''] = path.slice(restPrefix.length).split('/');
    return first === 'rpc' ? `rpc/${decodeSegment(second)}` : decodeSegment(first);
  };

  const sessionMissing = (resource: string): Response =>
    new Response(
      JSON.stringify({
        code: SESSION_MISSING_CODE,
        message: `No signed-in session: ${resource} was not requested`,
        details: null,
        hint: null,
      }),
      { status: 401, statusText: 'Unauthorized', headers: { 'Content-Type': 'application/json' } },
    );

  /** Drops the shared listener once nobody is waiting. */
  const releaseListener = (): void => {
    if (waiters.size > 0 || !unsubscribe) return;
    const stop = unsubscribe;
    unsubscribe = null;
    stop();
  };

  const settleAll = (session: Session | null): void => {
    const pending = [...waiters];
    waiters.clear();
    for (const waiter of pending) waiter.settle(session);
    releaseListener();
  };

  /** ONE auth listener for all waiters, however many requests are parked. */
  const ensureListener = (): void => {
    if (unsubscribe) return;
    // Registration replays INITIAL_SESSION with the CURRENT session, so this is
    // "check the session, then wait for it". A null INITIAL_SESSION mid-session
    // (refresh pending or in cooldown) is not an answer — keep waiting — unless
    // the tracker already knows we are signed out (cold load without a session).
    const stop = deps.onAuthStateChange((event, session) => {
      if (session) settleAll(session);
      else if (event === 'SIGNED_OUT' || signedOut) settleAll(null);
    });
    // A listener may answer synchronously during registration (test doubles do).
    if (waiters.size === 0) stop();
    else unsubscribe = stop;
  };

  const waitForSession = (signal: AbortSignal | null | undefined): Promise<Session | null> =>
    new Promise<Session | null>((resolve, reject) => {
      // Per-waiter timer, so a request parked late still gets the full waitMs.
      const timer = setTimeout(() => {
        waiters.delete(waiter);
        signal?.removeEventListener('abort', onAbort);
        resolve(null);
        releaseListener();
      }, waitMs);
      const onAbort = (): void => {
        clearTimeout(timer);
        waiters.delete(waiter);
        reject(abortErrorOf(signal));
        releaseListener();
      };
      const waiter: Waiter = {
        settle: (session) => {
          clearTimeout(timer);
          signal?.removeEventListener('abort', onAbort);
          resolve(session);
        },
      };
      waiters.add(waiter);
      signal?.addEventListener('abort', onAbort, { once: true });
      ensureListener();
    });

  const gatedFetch: typeof fetch = async (input, init) => {
    const resource = restResource(input);
    // Only PostgREST is gated; /auth/v1 MUST pass (see header: gating it deadlocks).
    if (resource === null || deps.publicRestResources.has(resource)) {
      return deps.baseFetch(input, init);
    }
    // fetch() semantics: init.headers, when given, replaces a Request's own headers.
    const headers = new Headers(init?.headers ?? (isRequest(input) ? input.headers : undefined));
    if (headers.get('Authorization') !== anonBearer) {
      return deps.baseFetch(input, init); // user JWT attached — happy path, untouched
    }

    if (signedOut) {
      deps.onGate?.(resource, 'blocked');
      return sessionMissing(resource);
    }

    const signal = init?.signal ?? (isRequest(input) ? input.signal : undefined);
    if (signal?.aborted) throw abortErrorOf(signal);

    deps.onGate?.(resource, 'waiting');
    const session = await waitForSession(signal);
    if (!session) {
      deps.onGate?.(resource, 'blocked');
      return sessionMissing(resource);
    }

    headers.set('Authorization', `Bearer ${session.access_token}`);
    deps.onGate?.(resource, 'resumed');
    return deps.baseFetch(input, { ...init, headers });
  };

  return {
    fetch: gatedFetch,
    markSignedOut: () => {
      signedOut = true;
      settleAll(null); // parked requests answer now instead of after waitMs
    },
    trackAuth: () => {
      if (tracking) return;
      tracking = true;
      deps.onAuthStateChange((event, session) => {
        if (session) signedOut = false;
        else if (event === 'SIGNED_OUT' || event === 'INITIAL_SESSION') signedOut = true;
      });
    },
  };
}
